import {Injectable} from '@angular/core';
import {HttpClient, HttpHeaders} from '@angular/common/http';
import {environment} from '../../../../../environments/environment';
import {ProductOverviewResponse} from '../../../../services/product-overview/product-overview-response.model';
import {ProductOverviewRequest} from '../../../../services/product-overview/product-overview-request.model';
import {DetailInfoService} from './details-info.service';
import {DetailScreenComponent} from '../detail-screen/detail-screen.component';
import {DetailScreensRequestModel} from "./detail-screens-request.model";
import {UrlRequestData} from "./detail-screen.model";
import {PRODUCT_VIEW_DETAILS_SCREENS, PRODUCT_VIEW_URI} from "./constants/product-view.constants";

@Injectable({
  providedIn: 'root',
})
export class DetailScreensService {

  civViewBaseUrl;
  civServicesBaseUrl;
  productViewDetailBaseUrl;
  productViewDetailScreensUrl;

  private readonly productViewDetailsScreens = PRODUCT_VIEW_DETAILS_SCREENS
  private readonly URI = PRODUCT_VIEW_URI

  constructor(private http: HttpClient, private readonly detailInfoService: DetailInfoService) {
    this.civViewBaseUrl = environment.civViewBaseURL;
    this.civServicesBaseUrl = environment.civServicesBaseURL;
    this.productViewDetailBaseUrl = environment.productViewDetailsBaseURL;
    this.productViewDetailScreensUrl = `${this.productViewDetailBaseUrl}details-screen`;
  }

  getDetailsPage(url: any, arrangementNumber: string, administrationCode: string,  iban: string, bankCode: string,
                 commercialProductTypeCode?: string, restrictedDataViewException?, relationMarketSegmentCode ?: string) {
    let requestBody;
    const detailsPageURL = this.productViewDetailsScreens.includes(url) ?
      this.productViewDetailScreensUrl :
      `${this.civServicesBaseUrl}${url}`;

    DetailScreensRequestModel.arrangementNumber = arrangementNumber;
    DetailScreensRequestModel.administrationCode = administrationCode;
    DetailScreensRequestModel.iban = iban;
    DetailScreensRequestModel.bankCode = bankCode;
    DetailScreensRequestModel.commercialProductTypeCode = commercialProductTypeCode;
    DetailScreensRequestModel.restrictedDataViewException = restrictedDataViewException;
    DetailScreensRequestModel.relationMarketSegmentCode = relationMarketSegmentCode || '';

    if (url === 'rekeningcourant') {
      iban = iban.replace(/\s/g, '');
      iban = iban.includes('(') ? iban : iban + '(EUR)';
      DetailScreensRequestModel.iban = iban;
    }

    if (url !== 'onlinekeys') {
      requestBody = this.createRequestBody(
        DetailScreensRequestModel.arrangementNumber,
        DetailScreensRequestModel.administrationCode,
        DetailScreensRequestModel.iban,
        DetailScreensRequestModel.bankCode,
        DetailScreensRequestModel.commercialProductTypeCode,
        DetailScreensRequestModel.restrictedDataViewException,
        url
      );
    } else {
      requestBody = {
        arrangementNumbers: this.detailInfoService.detailScreenInfo.arrangementNumbers || [],
        selectedPage: 1,
      };
    }

    const headers = this.createHeaders();
    return this.http.post<any>(detailsPageURL, requestBody, {headers});
  }

  getOldDetailsPage(url: string, arrangementNumber: string, administrationCode: string, commercialProductTypeCode: string,
                    parentCustomerId: string, listOfChildArrangementNumbers: string[]) {
    const detailsPageURL = this.civViewBaseUrl + 'productoverview/' + url;
    const requestBody = this.createOldRequestBody(arrangementNumber, administrationCode, commercialProductTypeCode, parentCustomerId,
      listOfChildArrangementNumbers);
    const headers = this.createHeaders();
    return this.http.post(detailsPageURL, requestBody, {headers, responseType: 'text'});
  }

  getOldTabPage(url: string) {
    const detailsPageURL = this.civViewBaseUrl + 'productoverview/' + url;
    const requestBody = this.createOldTabRequestBody();
    const headers = this.createHeaders();
    return this.http.post(detailsPageURL, requestBody, {headers, responseType: 'text'});
  }

  getTableData(url: string, arrangementNumber: string, administrationCode: string) {
    const tableDataURL = this.civServicesBaseUrl + url;
    const requestBody = this.createRequestBody(arrangementNumber, administrationCode, null, null, null, null);
    const headers = this.createHeaders();
    return this.http.post<any>(tableDataURL, requestBody, {headers});
  }

  getButtonUrl(data: UrlRequestData) {
    const buttonURL = this.civServicesBaseUrl + data.url;
    const requestBody = this.createButtonRequestBody(data);
    const headers = this.createHeaders();
    return this.http.post<any>(buttonURL, requestBody, {headers});
  }

  private createOldTabRequestBody(): string {
    if (!ProductOverviewRequest) {
      return;
    }
    const perspective = this.determinePerspective();
    const requestBody = {
      perspective,
      parametersToken: ProductOverviewRequest.parametersToken,
    };
    return JSON.stringify(requestBody);
  }

  private determinePerspective() {
    let perspective;
    if (ProductOverviewRequest.orgYN === 'Y') {
      perspective = 'ORG';
    } else {
      perspective = 'NP';
    }
    return perspective;
  }

  private createOldRequestBody(arrangementNumber: string, administrationCode: string, commercialProductTypeCode: string,
                               parentCustomerId: string, listOfChildArrangementNumbers): string {
    if (!ProductOverviewRequest) {
      return;
    }
    const perspective = this.determinePerspective();
    const requestBody = {
      arrangementNumber,
      administrationCode,
      commercialProductTypeCode,
      perspective,
      parentCustomerId,
      listOfChildArrangementNumbers,
      restrictedDataViewException: ProductOverviewResponse.restrictedDataViewException,
      relationRestrictedDataView: ProductOverviewResponse.relationRestrictedDataView,
      relationMarketSegmentCode: ProductOverviewResponse.relationMarketSegmentCode,
      parametersToken: ProductOverviewRequest.parametersToken,
    };
    return JSON.stringify(requestBody);
  }

  private createRequestBody(arrangementNumber, administrationCode, iban, bankCode,
                            commercialProductTypeCode, restrictedDataViewException, url?: any): string {
    if (!ProductOverviewRequest) {
      return;
    }
    const relationRestrictedView = ProductOverviewResponse.relationRestrictedDataView;
    DetailScreensRequestModel.relationRestrictedDataView = relationRestrictedView;
    const requestBody = {
      arrangementNumber,
      administrationCode,
      commercialProductTypeCode,
      restrictedDataViewException,
      relationRestrictedDataView: ProductOverviewResponse.relationRestrictedDataView,
      relationMarketSegmentCode: ProductOverviewResponse.relationMarketSegmentCode,
      parametersToken: ProductOverviewRequest.parametersToken,
    };

    if ((url && url !== '') && this.productViewDetailsScreens.includes(url)) {
      const detailScreenRequestBody = {
        arrangementNumber,
        administrationCode,
        iban,
        bankCode,
        commercialProductTypeCode,
        screenName: this.URI[url],
        orgYN: ProductOverviewRequest.orgYN
      }
      return JSON.stringify(detailScreenRequestBody);
    }

    return JSON.stringify(requestBody);
  }

  private createButtonRequestBody(reqBodyParams: UrlRequestData): string {
    if (!ProductOverviewRequest) {
      return;
    }

    if (reqBodyParams.data) {
      return this.createRequestBodyOfExtraData(reqBodyParams);
    }

    const requestBody = {
      arrangementNumber: reqBodyParams.arrangementNumber,
      administrationCode: reqBodyParams.administrationCode,
      commercialProductTypeCode: reqBodyParams.commercialProductTypeCode,
      parametersToken: ProductOverviewRequest.parametersToken,
    };

    if (reqBodyParams.tokenSort) {
      requestBody['tokenSort'] = reqBodyParams.tokenSort;
    }

    return JSON.stringify(requestBody);
  }

  createRequestBodyOfExtraData(reqBody): string {
    const params = {
      ...reqBody.data,
    };
    return JSON.stringify(params);
  }

  private createHeaders(): HttpHeaders {
    let headers = new HttpHeaders();
    headers = headers.append('Authorization', ProductOverviewRequest.token);
    headers = headers.append('MFA-Authorization', ProductOverviewRequest.token);
    headers = headers.append('Graph-Authorization', ProductOverviewRequest.graphToken);
    headers = headers.append('Content-type', 'application/json');
    return headers;
  }

  public clearSecondDetailsScreen(secondDetailsComponent: DetailScreenComponent) {
    secondDetailsComponent.showDetails = false;
    secondDetailsComponent.changeRef.detectChanges();
  }
}
